import React, { useEffect, useRef, useState } from "react";
import { CiFilter } from "react-icons/ci";
import { MdOutlineChevronRight } from "react-icons/md";
import { PiShareFat, PiShareFatFill } from "react-icons/pi"; // thicker share icon
import { TbFlag, TbFlagFilled, TbMinusVertical } from "react-icons/tb";
import { RiBookmarkLine, RiBookmarkFill } from "react-icons/ri";
import { toast } from "react-toastify";
import CreateCompanyFooter from "../components/createcompanyfooter/CreateCompanyFooter";
import { Link, useLocation, useNavigate } from "react-router-dom";
import ManagerProgressBar from "./ManagerProgressBar";
import ManagerReviewSection from "./ManagerReviewSection";
import { getApiBaseUrl } from "../apiBaseUrlFinder";
import Icon from "@mdi/react";
import { mdiLinkedin } from "@mdi/js";
import { Helmet } from "react-helmet";

const ManagerProfile = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const [managerData, setManagerData] = useState(null);
  const [isReported, setIsReported] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [subscriptionEmail, setSubscriptionEmail] = useState("");
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [watchedValues, setWatchedValues] = useState({ subscriptionEmail: "" });

  const managerName = queryParams.get("name") || "";
  const managerId = queryParams.get("id") || "";

  // anchor for scrolling to reviews
  const reviewsTopRef = useRef(null);

  // Get auth token from localStorage
  const getAuthToken = () => localStorage.getItem("token");

  // Check if user is logged in
  const isUserLoggedIn = () => !!getAuthToken();

  // Helper function to check if field is active (for form styling)
  const isFieldActive = (fieldName) => {
    const value = watchedValues?.[fieldName];
    const isFocused = focusedField === fieldName;
    return (value && value.trim() !== "") || isFocused;
  };

  useEffect(() => {
    // Update browser URL for shareable /manager links
    if (location.pathname === "/manager" && managerName && managerId) {
      const newUrl = `/manager?name=${encodeURIComponent(
        managerName
      )}&id=${encodeURIComponent(managerId)}`;
      window.history.replaceState({}, "", newUrl);
    }

    if (managerId && managerName) {
      fetchManagerProfile(managerId, managerName);
      if (isUserLoggedIn()) {
        checkIfManagerSaved(managerId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [managerId, managerName, location.pathname]);

  const fetchManagerProfile = async (id, name) => {
    const apiUrl = `${getApiBaseUrl()}/manager_profile?manager_id=${encodeURIComponent(
      id
    )}&manager_full_name=${encodeURIComponent(name)}`;
    try {
      const response = await fetch(apiUrl);
      const data = await response.json();
      setManagerData(data);
    } catch (error) {
      console.error("Error fetching manager profile:", error);
    }
  };

  const checkIfManagerSaved = async (mgrId) => {
    const token = getAuthToken();
    if (!token) return;

    try {
      const response = await fetch(
        `${getApiBaseUrl()}/get_user_saved_manager?manager_id=${encodeURIComponent(
          mgrId
        )}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setIsSaved(data?.manager_saved === true ?? false);
      }
    } catch (error) {
      console.error("Error checking if manager is saved:", error);
    }
  };

  const showFlag = (message) =>
    toast(message, {
      position: "top-center",
      autoClose: 1800,
      hideProgressBar: true,
      closeButton: false,
      draggable: false,
      className: "flag-toast",
      bodyClassName: "flag-toast-body",
      icon: <span className="flag-dot" />,
    });

  const handleSaveManager = async () => {
    if (!isUserLoggedIn()) {
      toast.info("Please log in to save managers", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }
    if (!managerData || isSaving) return;

    setIsSaving(true);
    const token = getAuthToken();

    try {
      if (isSaved) {
        toast.info("Removing manager from saved list...", {
          position: "top-right",
          autoClose: false,
          toastId: "unsave-processing",
        });

        const response = await fetch(
          `${getApiBaseUrl()}/remove_saved_manager`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ manager_id: managerId }),
          }
        );

        if (response.ok) {
          setIsSaved(false);
          toast.dismiss("unsave-processing");
          toast.success("Manager removed from saved list!", {
            position: "top-right",
            autoClose: 3000,
          });
        } else {
          toast.dismiss("unsave-processing");
          toast.error("Failed to remove manager from saved list", {
            position: "top-right",
            autoClose: 3000,
          });
        }
      } else {
        toast.info("Saving manager to your list...", {
          position: "top-right",
          autoClose: false,
          toastId: "save-processing",
        });

        const response = await fetch(`${getApiBaseUrl()}/save_manager`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            manager_id: managerId,
            manager_full_name: managerData.manager_full_name,
            company_name: managerData.company_name,
            company_id: managerData.company_id || "",
            first_name: managerData.first_name || "",
            last_name: managerData.last_name || "",
            rating: managerData.manager_average_rating || 5,
            location: managerData.location || "",
            review_count: managerData.reviews?.length || 0,
            linkedin_profile: managerData.linkedin_profile || "",
            company_logo_url: managerData.company_logo_url || "",
          }),
        });

        if (response.ok) {
          setIsSaved(true);
          toast.dismiss("save-processing");
          toast.success("Manager saved successfully!", {
            position: "top-right",
            autoClose: 3000,
          });
        } else {
          toast.dismiss("save-processing");
          toast.error("Failed to save manager", {
            position: "top-right",
            autoClose: 3000,
          });
        }
      }
    } catch (error) {
      console.error("Error saving/unsaving manager:", error);
      toast.dismiss("save-processing");
      toast.dismiss("unsave-processing");
      toast.error("An error occurred. Please try again.", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const navigate = useNavigate();
  const handleRateClick = () => {
    if (managerId && managerName) {
      const params = new URLSearchParams({
        name: managerName,
        id: managerId,
      }).toString();
      navigate(`/submit-manager-review?${params}`);
    } else {
      console.error("Manager data is missing.");
    }
  };

  const handleReportClick = async () => {
    if (managerId && managerName) {
      try {
        const response = await fetch(`${getApiBaseUrl()}/manager_reported`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            manager_full_name: managerName,
            manager_id: managerId,
          }),
        });

        if (response.ok) {
          setIsReported(true);
          showFlag("Profile reported");
        } else {
          console.error("Failed to report manager");
          toast.error("Failed to report manager", { position: "top-center" });
        }
      } catch (error) {
        console.error("Error reporting manager:", error);
        toast.error("Failed to report manager", { position: "top-center" });
      }
    }
  };

  const handleCopy = () => {
    navigator.clipboard
      .writeText(window.location.href)
      .then(() => {
        setCopied(true);
        showFlag("Profile link copied");
      })
      .catch((err) => console.error("Failed to copy:", err));

    setTimeout(() => setCopied(false), 5000);
  };

  // Sort button click
  const handleSortClick = () => {
    showFlag("This feature is coming soon");
  };

  // Smoothly scroll to the reviews anchor (adjust for sticky headers if needed)
  const scrollToReviews = () => {
    const anchor = reviewsTopRef.current;
    if (!anchor) return;
    const headerOffset = 80; // tweak if you have a taller navbar
    const y =
      anchor.getBoundingClientRect().top + window.pageYOffset - headerOffset;
    window.scrollTo({ top: y, behavior: "smooth" });
  };

  // Email validation function
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (e) => {
    const email = e.target.value;
    setSubscriptionEmail(email);
    setIsEmailValid(validateEmail(email));
    setWatchedValues(prev => ({ ...prev, subscriptionEmail: email }));
  };

  const handleSubscriptionSubmit = async (e) => {
    e.preventDefault();
    if (subscriptionEmail.trim() && isEmailValid && !isSubscribing) {
      setIsSubscribing(true);

      // Show loading toast
      toast.info("Subscribing...", {
        position: "top-right",
        autoClose: false,
        toastId: "subscribe-processing",
      });

      try {
        const response = await fetch(`${getApiBaseUrl()}/manager_subscribe`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            subscriber_email: subscriptionEmail,
            manager_name: managerName,
            manager_id: managerId,
            company_name: managerData?.company_name || "",
            company_id: managerData?.company_id || "",
          }),
        });

        if (response.ok) {
          toast.dismiss("subscribe-processing");
          toast.success("Subscribed! You'll be notified when new reviews are posted.", {
            position: "top-right",
            autoClose: 3000,
          });
          setSubscriptionEmail("");
          setIsEmailValid(false);
        } else {
          toast.dismiss("subscribe-processing");
          toast.error("Failed to subscribe. Please try again.", {
            position: "top-right",
            autoClose: 3000,
          });
        }
      } catch (error) {
        console.error("Error subscribing:", error);
        toast.dismiss("subscribe-processing");
        toast.error("An error occurred. Please try again.", {
          position: "top-right",
          autoClose: 3000,
        });
      } finally {
        setIsSubscribing(false);
      }
    }
  };

  return (
    <>
      <style>
        {`
          @keyframes pulse {
            0% { opacity:1; transform:scale(1); }
            50% { opacity:.5; transform:scale(1.2); }
            100% { opacity:1; transform:scale(1); }
          }

          .flag-toast {
            background:#fff !important;
            color:#111 !important;
            border-left:4px solid #16a34a !important;
            border-radius:10px !important;
            box-shadow:0 6px 18px rgba(0,0,0,.12) !important;
            padding:.75rem 1rem !important;
            min-width: 220px;
          }
          .flag-toast-body { font-weight:600; }
          .flag-dot {
            width:10px; height:10px; display:inline-block;
            border-radius:50%; background:#16a34a; margin-right:.5rem;
          }

          .cta-link{
            display:inline-flex;
            align-items:center;
            gap:.25rem;
            text-decoration: underline;
            text-underline-offset: 2px;
            transition: transform .15s ease, text-decoration-thickness .15s ease, color .15s ease, box-shadow .2s ease;
            will-change: transform;
          }
          .cta-link:hover{
            transform: translateY(-2px);
            text-decoration-thickness: 2px;
            color:#111;
          }
          .cta-link:active{
            transform: translateY(0) scale(.98);
          }
          .cta-link:focus-visible{
            outline:2px solid #111;
            outline-offset:2px;
            border-radius:6px;
          }
          .link-reset{
            background:none;
            border:none;
            padding:0;
            margin:0;
            color:inherit;
            font:inherit;
            cursor:pointer;
          }
          @media (prefers-reduced-motion: reduce){
            .cta-link{ transition:none; }
          }
        `}
      </style>

      {managerData && (
        <Helmet>
          <title>{`${managerData.manager_full_name} – ${managerData.manager_position} | Rate My Manager`}</title>
          <meta
            name="description"
            content={`Reviews and ratings for ${managerData.manager_full_name}, ${managerData.manager_position} at ${managerData.company_name}.`}
          />
          <meta
            property="og:title"
            content={`${managerData.manager_full_name} – ${managerData.manager_position}`}
          />
          <meta
            property="og:description"
            content={`Reviews for ${managerData.manager_full_name} at ${managerData.company_name}`}
          />
        </Helmet>
      )}

      <div className="container">
        <div className="row">
          <div className="col-12">
            {/* Header section */}
            <div className="row pt-lg-5 pt-md-4 pt-3">
              <div className="col-lg-6 col-md-6 col-12">
                <div className="d-flex align-items-end">
                  <h3 className="rating_review_h3 mb-0">
                    {managerData?.manager_average_rating === 0
                      ? 5
                      : managerData?.manager_average_rating}
                  </h3>
                  <h6 className="add_review_rate_h3 ps-2">/ 5</h6>
                </div>
                <p className="company_profile_rating_p pt-2">
                  {managerData?.reviews?.length}{" "}
                  <button
                    type="button"
                    className="cta-link link-reset"
                    style={{ fontWeight: "bold" }}
                    onClick={scrollToReviews}
                    aria-label="Jump to ratings section"
                    title="Jump to ratings"
                  >
                    ratings
                  </button>
                </p>

                <div className="d-flex align-items-end pt-2">
                  <h3 className="company_management_h3 mb-1">
                    {managerData?.manager_full_name}
                  </h3>
                </div>

                <div className="pt-1">
                  <div className="d-flex align-items-center pt-1">
                    <Link
                      to={`/company-profile?name=${encodeURIComponent(
                        managerData?.company_name
                      )}&id=${managerData?.company_id}`}
                    >
                      {managerData?.company_logo_url && (
                        <img
                          src={managerData.company_logo_url}
                          alt={`${managerData.company_name} Logo`}
                          style={{
                            width: "55px",
                            height: "55px",
                            objectFit: "contain",
                            borderRadius: "8px",
                            marginRight: "10px",
                            marginBottom: "10px",
                          }}
                        />
                      )}
                    </Link>
                    <p className="manager_company_p mb-0">
                      {managerData?.manager_position} <br />
                      at{" "}
                      <Link
                        className="manager_company_p"
                        to={`/company-profile?name=${encodeURIComponent(
                          managerData?.company_name
                        )}&id=${managerData?.company_id}`}
                      >
                        {managerData?.company_name}
                      </Link>
                    </p>
                  </div>
                </div>

                <p className="company_profile_rating_p">
                  Rate <span className="text-black fw-bold">anonymously!</span>
                </p>

                <div className="d-flex align-items-center">
                  <button
                    className="rate_company_profile_btn"
                    onClick={handleRateClick}
                    type="button"
                    title="Rate this manager"
                  >
                    Rate
                    <MdOutlineChevronRight className="ms-1 fs-5" />
                  </button>

                  <div className="d-flex ms-3">
                    {/* Share */}
                    <div
                      onClick={handleCopy}
                      className="icon-btn"
                      style={{ cursor: "pointer" }}
                      title={copied ? "Link copied" : "Copy link"}
                      aria-label={copied ? "Link copied" : "Copy link"}
                    >
                      {copied ? (
                        <PiShareFatFill fontSize={25} color="#151515" />
                      ) : (
                        <PiShareFat fontSize={25} color="#151515" />
                      )}
                    </div>

                    <TbMinusVertical fontSize={25} color="#AFAFAF" />

                    {/* Report */}
                    <div
                      onClick={handleReportClick}
                      className="icon-btn"
                      style={{ cursor: "pointer" }}
                      title={isReported ? "Reported" : "Report profile"}
                      aria-label={isReported ? "Reported" : "Report profile"}
                    >
                      {isReported ? (
                        <TbFlagFilled fontSize={25} color="#151515" />
                      ) : (
                        <TbFlag fontSize={25} color="#151515" />
                      )}
                    </div>

                    {managerData?.linkedin_profile && (
                      <>
                        <TbMinusVertical fontSize={25} color="#AFAFAF" />
                        <a
                          href={managerData.linkedin_profile}
                          rel="noopener noreferrer"
                          target="_blank"
                          className="icon-btn linkedin-icon"
                          title="Open LinkedIn profile"
                          aria-label="Open LinkedIn profile"
                          style={{ cursor: "pointer" }}
                        >
                          <Icon path={mdiLinkedin} size={1.1} color="#0A66C2" />
                        </a>
                      </>
                    )}

                    {/* Save/Unsave Manager Button */}
                    {isUserLoggedIn() && (
                      <>
                        <TbMinusVertical fontSize={25} color="#AFAFAF" />
                        <div
                          onClick={isSaving ? undefined : handleSaveManager}
                          className="icon-btn save-icon"
                          style={{
                            cursor: isSaving ? "not-allowed" : "pointer",
                            opacity: isSaving ? 0.6 : 1,
                            position: "relative",
                          }}
                          title={
                            isSaving
                              ? "Processing..."
                              : isSaved
                              ? "Remove from saved"
                              : "Save manager"
                          }
                          aria-label={
                            isSaved ? "Remove from saved" : "Save manager"
                          }
                        >
                          {isSaved ? (
                            <RiBookmarkFill size={22} color="#151515" />
                          ) : (
                            <RiBookmarkLine size={22} color="#151515" />
                          )}
                          {isSaving && (
                            <div
                              style={{
                                position: "absolute",
                                top: "-2px",
                                right: "-2px",
                                width: "8px",
                                height: "8px",
                                backgroundColor: "#007bff",
                                borderRadius: "50%",
                                animation: "pulse 1.5s infinite",
                              }}
                            />
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="col-lg-6 col-md-6 col-12 pt-lg-0 pt-md-0 pt-4">
                <ManagerProgressBar
                  reviewDistribution={managerData?.review_distribution}
                />
              </div>
            </div>

            {/* Tags & Reviews */}
            <div className="row pt-lg-5 pt-md-4 pt-3">
              <div className="col-lg-9 col-md-9 col-12">
                <h3 className="top_tags_h3">Top Tags</h3>
                <div className="d-lg-flex d-md-flex align-items-center">
                  <div>
                    <div className="d-flex flex-wrap gap-2 pt-2">
                      {managerData?.top_positive_tags?.map((tag) => (
                        <div
                          key={tag}
                          className="px-3 py-1"
                          style={{
                            backgroundColor: "#EDFDED",
                            borderRadius: "30px",
                            fontSize: "0.85em",
                            color: "#2C2C2C",
                            height: "28px",
                          }}
                        >
                          {tag}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="border_difference_tags ms-2 mt-2"></div>

                  <div>
                    <div className="d-flex flex-wrap gap-2 pt-2 ps-2">
                      {managerData?.top_negative_tags?.map((tag) => (
                        <div
                          key={tag}
                          className="px-3 py-1"
                          style={{
                            backgroundColor: "#FFD0CA",
                            borderRadius: "30px",
                            fontSize: "0.85em",
                            color: "#2C2C2C",
                            height: "28px",
                          }}
                        >
                          {tag}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Subscription form for screens < 1200px */}
                <div className="d-xl-none mt-4">
                  <div className="mfn-subscription-container">
                    <h6 className="mfn-subscription-title">
                      Get notified when new reviews are posted
                    </h6>
                    <form
                      onSubmit={handleSubscriptionSubmit}
                      className="mfn-subscription-form"
                    >
                      <div className="form-group">
                        <input
                          type="email"
                          id="subscriptionEmail"
                          className={`form-input dropdown-toggle ${
                            isFieldActive("subscriptionEmail") ? "input-active" : ""
                          } label-medium`}
                          placeholder=" "
                          value={subscriptionEmail}
                          onChange={handleEmailChange}
                          onFocus={() => setFocusedField("subscriptionEmail")}
                          onBlur={() => setFocusedField(null)}
                        />
                        <label
                          htmlFor="subscriptionEmail"
                          className={`form-label ${
                            isFieldActive("subscriptionEmail") ? "label-active" : ""
                          }`}
                        >
                          Personal Email
                        </label>
                      </div>
                      <div className="mfn-subscription-button-wrapper">
                        <button
                          type="submit"
                          disabled={!isEmailValid || isSubscribing}
                          className="mfn-subscription-button"
                        >
                          {isSubscribing ? "Subscribing..." : "Submit"}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>

                {/* Ratings header + Sort */}
                <div className="pt-lg-5 pt-md-4 pt-3">
                  <div className="d-flex justify-content-between align-items-center h-100">
                    <h5 className="ratings_h5_manager_profile mb-0">
                      {managerData?.reviews?.length} Ratings
                    </h5>
                    <button
                      type="button"
                      className="btn btn_sort"
                      onClick={handleSortClick}
                      title="This feature is coming soon"
                      aria-label="Sort coming soon"
                    >
                      <CiFilter fontWeight={700} color="#6B6B6B" /> Sort
                    </button>
                  </div>
                  <div className="border_bottom_reviews mt-1"></div>
                </div>

                {/* anchor: scroll lands here */}
                <div ref={reviewsTopRef} />

                <ManagerReviewSection
                  managerData={managerData}
                  setManagerData={setManagerData}
                />
              </div>

              <div className="col-lg-3 col-md-3 col-12 d-none d-xl-block">
                <div className="mfn-subscription-container">
                  <h6 className="mfn-subscription-title">
                    Get notified when new reviews are posted
                  </h6>
                  <form
                    onSubmit={handleSubscriptionSubmit}
                    className="mfn-subscription-form"
                  >
                    <div className="form-group">
                      <input
                        type="email"
                        id="subscriptionEmailDesktop"
                        className={`form-input dropdown-toggle ${
                          isFieldActive("subscriptionEmail") ? "input-active" : ""
                        } label-medium`}
                        placeholder=" "
                        value={subscriptionEmail}
                        onChange={handleEmailChange}
                        onFocus={() => setFocusedField("subscriptionEmail")}
                        onBlur={() => setFocusedField(null)}
                      />
                      <label
                        htmlFor="subscriptionEmailDesktop"
                        className={`form-label ${
                          isFieldActive("subscriptionEmail") ? "label-active" : ""
                        }`}
                      >
                        Personal Email
                      </label>
                    </div>
                    <div className="mfn-subscription-button-wrapper">
                      <button
                        type="submit"
                        disabled={!isEmailValid || isSubscribing}
                        className="mfn-subscription-button"
                      >
                        {isSubscribing ? "Subscribing..." : "Submit"}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <CreateCompanyFooter />
    </>
  );
};

export default ManagerProfile;
